// SATELLITE-REGISTRY (docs/design/2026-10-30-central-agent-dispatcher.md §4, §9): Operator ›
// Platform › Satellites — the hosts that run Hermes containers and the agent endpoints on them.
// Platform admins only (as Operator › Connections: an agency admin whose agency holds a live
// operator grant on the platform's own company); writing an endpoint also needs the agency's
// live operator grant on the endpoint's company. Other agency users 403, anyone else 404.
//
// A gateway key (`api_key`) is accepted once, sealed in the keyring and never returned: no view
// here carries it. The one route that hands it out is the system agent's own (its runtime key
// only, for its own profile), so the runtime node keeps no Hermes directory file.
import { z } from "zod";
import {
  AgentEndpointStatus,
  RegistrySource,
  SatelliteReleaseAction,
  SatelliteReleaseComponent,
  SatelliteStatus,
  SatelliteTransport,
} from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { defineRoute } from "./route";

const base = "/v1/operator";
const core = { feature: "core", tag: "operator", access: "operator" } as const;

export const AgentEndpointView = z
  .object({
    id: Uuid,
    company_id: Uuid,
    de_membership_id: Uuid,
    satellite_id: Uuid,
    instance_id: z.string(),
    profile_name: z.string(),
    /** The container's gateway; a multiplexed one serves the profile at `/p/<profile>`. */
    api_base_url: z.string(),
    multiplex: z.boolean(),
    source: RegistrySource,
    status: AgentEndpointStatus,
    created_at: Timestamp,
    /**
     * SATELLITE-REGISTRY-2: what the last endpoint probe saw (an authenticated, model-free read
     * with the sealed key). `current` is true while it was taken against today's routing
     * generation (a new key or a retire fences it); null: never probed.
     */
    probe: z
      .object({
        at: Timestamp,
        ok: z.boolean(),
        detail: z.string().nullable(),
        current: z.boolean(),
      })
      .nullable(),
  })
  .meta({ id: "AgentEndpointView" });

/**
 * SATELLITE-RUNTIME: one component on one satellite. `state`: `current` (the installed version is
 * the pin, no drift), `drift` (files or keys differ from the release), `behind` (another version
 * than the pin), `unreported` (pinned, never reported), `unpinned` (reported, nothing pinned).
 */
export const SatelliteReleaseView = z
  .object({
    component: SatelliteReleaseComponent,
    pinned: z.object({ version: z.string(), sha256: z.string() }).nullable(),
    installed: z
      .object({
        version: z.string(),
        sha256: z.string().nullable(),
        action: SatelliteReleaseAction,
        reported_at: Timestamp,
        detail: z.string().nullable(),
      })
      .nullable(),
    drift: z.array(z.string()),
    state: z.enum(["current", "drift", "behind", "unreported", "unpinned"]),
  })
  .meta({ id: "SatelliteReleaseView" });

export const SatelliteView = z
  .object({
    id: Uuid,
    /** The owning company; null: the platform's own capacity. */
    company_id: Uuid.nullable(),
    name: z.string(),
    transport: SatelliteTransport,
    base_address: z.string(),
    health_path: z.string(),
    status: SatelliteStatus,
    max_concurrent_runs: z.int().min(1),
    source: RegistrySource,
    panel_node_id: z.string().nullable(),
    last_seen_at: Timestamp.nullable(),
    last_probe_at: Timestamp.nullable(),
    last_probe_detail: z.string().nullable(),
    probe_misses: z.int().min(0),
    retired_at: Timestamp.nullable(),
    retired_reason: z.string().nullable(),
    created_at: Timestamp,
    /** Its active endpoints (never a key). */
    endpoints: z.array(AgentEndpointView),
    /**
     * SATELLITE-RUNTIME: what it runs of each component, against the platform's pin (a component
     * the panel never reported is listed only while a pin exists).
     */
    releases: z.array(SatelliteReleaseView),
  })
  .meta({ id: "SatelliteView" });

export const SatelliteList = z
  .object({ data: z.array(SatelliteView) })
  .meta({ id: "SatelliteList" });

export const SatelliteParams = z.object({ id: Uuid });

const Slots = z.int().min(1).max(1000);
const HealthPath = z
  .string()
  .max(121)
  .regex(/^\/[A-Za-z0-9._/-]{0,120}$/)
  .describe("The path the liveness probe GETs (default /health)");

export const SatelliteCreate = z
  .strictObject({
    name: z.string().trim().min(1).max(120),
    /** The owning company; omitted or null: the platform's own capacity. */
    company_id: Uuid.nullable().optional(),
    transport: SatelliteTransport,
    base_address: z.string().trim().min(1).max(255),
    max_concurrent_runs: Slots.optional(),
    health_path: HealthPath.optional(),
  })
  .meta({ id: "SatelliteCreate" });

export const SatelliteUpdate = z
  .strictObject({
    name: z.string().trim().min(1).max(120).optional(),
    max_concurrent_runs: Slots.optional(),
    /** true: no new runs (drain); false: back to live (the probe decides from there). */
    draining: z.boolean().optional(),
  })
  .meta({ id: "SatelliteUpdate" });

export const RegistryRetire = z
  .strictObject({ reason: z.string().trim().min(1).max(500) })
  .meta({ id: "RegistryRetire" });

export const SatelliteRetired = z
  .object({ satellite: SatelliteView, endpoints_retired: z.int().min(0), already: z.boolean() })
  .meta({ id: "SatelliteRetired" });

export const SatelliteProbeResult = z
  .object({
    satellite_id: Uuid,
    status: SatelliteStatus,
    previous: SatelliteStatus,
    seen: z.boolean(),
    detail: z.string(),
  })
  .meta({ id: "SatelliteProbeResult" });

export const AgentEndpointCreate = z
  .strictObject({
    company_id: Uuid,
    de_membership_id: Uuid,
    satellite_id: Uuid,
    instance_id: z.string().trim().min(1).max(128),
    profile_name: z
      .string()
      .max(128)
      .regex(/^(?!\.{1,2}$)[A-Za-z0-9._-]{1,128}$/),
    /** The container gateway's origin (scheme, host, port; no path). */
    api_base_url: z.string().trim().min(1).max(500),
    multiplex: z.boolean().default(true),
    /** The profile's `API_SERVER_KEY`: sealed at once, never shown again. */
    api_key: z.string().min(16).max(256),
  })
  .meta({ id: "AgentEndpointCreate" });

export const AgentEndpointRetireParams = z.object({ id: Uuid });

/**
 * SATELLITE-REGISTRY-2: register many profiles of ONE container at once (HTVA's 26 profiles on
 * its two containers: one call per container). A profile names its DE through the company's
 * allocated panel profile, or `de_membership_id` for an agent with none (the system agent).
 * Each item is registered in its own transaction and answers on its own; `dry_run` validates
 * and resolves without writing.
 */
export const AgentEndpointBulk = z
  .strictObject({
    company_id: Uuid,
    satellite_id: Uuid,
    instance_id: z.string().trim().min(1).max(128),
    api_base_url: z.string().trim().min(1).max(500),
    multiplex: z.boolean().default(true),
    dry_run: z.boolean().default(false),
    endpoints: z
      .array(
        z.strictObject({
          profile_name: z
            .string()
            .max(128)
            .regex(/^(?!\.{1,2}$)[A-Za-z0-9._-]{1,128}$/),
          de_membership_id: Uuid.optional(),
          api_key: z.string().min(16).max(256),
        }),
      )
      .min(1)
      .max(100),
  })
  .meta({ id: "AgentEndpointBulk" });

export const AgentEndpointBulkResult = z
  .object({
    dry_run: z.boolean(),
    created: z.int().min(0),
    unchanged: z.int().min(0),
    failed: z.int().min(0),
    results: z.array(
      z.object({
        profile_name: z.string(),
        de_membership_id: Uuid.nullable(),
        endpoint_id: Uuid.nullable(),
        outcome: z.enum(["created", "unchanged", "would_create", "error"]),
        /** The refusal's sentence; never a key. */
        problem: z.string().nullable(),
      }),
    ),
  })
  .meta({ id: "AgentEndpointBulkResult" });

export const AgentEndpointProbeResult = z
  .object({
    endpoint_id: Uuid,
    profile_name: z.string(),
    ok: z.boolean(),
    detail: z.string(),
  })
  .meta({ id: "AgentEndpointProbeResult" });

/**
 * The system agent's gateway; never cached. `api_key` only for the agent's own key (its runtime);
 * a platform admin sees where it runs, never the key (null).
 */
export const SystemAgentEndpoint = z
  .object({
    api_base_url: z.string(),
    api_key: z.string().nullable(),
    allow_plain_http: z.boolean(),
  })
  .meta({ id: "SystemAgentEndpoint" });

const SystemAgentParams = z.object({ company_id: Uuid, membership_id: Uuid });

export const satelliteRoutes = {
  "operator.satellites.list": defineRoute({
    ...core,
    id: "operator.satellites.list",
    method: "get",
    path: `${base}/satellites`,
    summary: "Every satellite with its status, slots, last seen and active endpoints (no keys)",
    response: { status: 200, schema: SatelliteList },
    errors: [],
    emits: [],
  }),
  "operator.satellites.create": defineRoute({
    ...core,
    id: "operator.satellites.create",
    method: "post",
    path: `${base}/satellites`,
    summary: "Register a satellite (platform-owned, or one company's)",
    body: SatelliteCreate,
    // Control-plane rows and a `cp.platform_audit` row; no company event.
    safe: true,
    response: { status: 201, schema: SatelliteView },
    errors: ["conflict"],
    emits: [],
  }),
  "operator.satellites.update": defineRoute({
    ...core,
    id: "operator.satellites.update",
    method: "patch",
    path: `${base}/satellites/{id}`,
    summary: "Rename a satellite, set its run slots, drain or undrain it",
    params: SatelliteParams,
    body: SatelliteUpdate,
    safe: true,
    response: { status: 200, schema: SatelliteView },
    errors: ["conflict"],
    emits: [],
  }),
  "operator.satellites.retire": defineRoute({
    ...core,
    id: "operator.satellites.retire",
    method: "post",
    path: `${base}/satellites/{id}/retire`,
    summary:
      "Retire a satellite: its endpoints are retired and their companies' routing evidence invalidated",
    params: SatelliteParams,
    body: RegistryRetire,
    safe: true,
    response: { status: 200, schema: SatelliteRetired },
    errors: [],
    emits: [],
  }),
  "operator.satellites.probe": defineRoute({
    ...core,
    id: "operator.satellites.probe",
    method: "post",
    path: `${base}/satellites/{id}/probe`,
    summary: "Run the liveness probe on one satellite now (no key sent, redirects not followed)",
    params: SatelliteParams,
    safe: true,
    response: { status: 200, schema: SatelliteProbeResult },
    errors: [],
    emits: [],
  }),
  "operator.agentEndpoints.create": defineRoute({
    ...core,
    id: "operator.agentEndpoints.create",
    method: "post",
    path: `${base}/agent-endpoints`,
    summary:
      "Register a DE's Hermes endpoint on a satellite (the gateway key is sealed, never shown); replaces its active one",
    body: AgentEndpointCreate,
    safe: true,
    response: { status: 201, schema: AgentEndpointView },
    errors: ["conflict"],
    emits: [],
  }),
  "operator.agentEndpoints.bulk": defineRoute({
    ...core,
    id: "operator.agentEndpoints.bulk",
    method: "post",
    path: `${base}/agent-endpoints/bulk`,
    summary:
      "Register many profiles of one container at once (keys sealed, never shown); per-item results, optional dry run",
    body: AgentEndpointBulk,
    safe: true,
    response: { status: 200, schema: AgentEndpointBulkResult },
    errors: ["conflict"],
    emits: [],
  }),
  "operator.agentEndpoints.probe": defineRoute({
    ...core,
    id: "operator.agentEndpoints.probe",
    method: "post",
    path: `${base}/agent-endpoints/{id}/probe`,
    summary:
      "Probe one endpoint now: an authenticated, model-free read at its gateway with the sealed key; the result is stored as the endpoint's evidence",
    params: AgentEndpointRetireParams,
    safe: true,
    response: { status: 200, schema: AgentEndpointProbeResult },
    errors: [],
    emits: [],
  }),
  "operator.agentEndpoints.retire": defineRoute({
    ...core,
    id: "operator.agentEndpoints.retire",
    method: "post",
    path: `${base}/agent-endpoints/{id}/retire`,
    summary: "Retire an agent endpoint (its company's routing evidence is invalidated)",
    params: AgentEndpointRetireParams,
    body: RegistryRetire,
    safe: true,
    response: { status: 200, schema: AgentEndpointView },
    errors: [],
    emits: [],
  }),
  "operator.systemAgents.endpoint": defineRoute({
    ...core,
    id: "operator.systemAgents.endpoint",
    method: "get",
    path: `${base}/system-agents/{company_id}/{membership_id}/endpoint`,
    summary:
      "The system agent's Hermes gateway from the registry: the key only to its own runtime key; a platform admin sees the URL, never the key",
    params: SystemAgentParams,
    response: { status: 200, schema: SystemAgentEndpoint },
    errors: ["not_found"],
    emits: [],
  }),
};
